"use client"

import { useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { DocumentType, DOCUMENT_TYPE_LABELS } from "@/types/document"
import { PenTool, Image, CreditCard, FileText, Paperclip } from "lucide-react"

interface DocumentTypeSelectorProps {
  isOpen: boolean
  onSelect: (type: DocumentType) => void
  onDismiss: () => void
}

const DOCUMENT_TYPE_ICONS: Record<DocumentType, React.ReactNode> = {
  [DocumentType.CUSTOMER_SIGNATURE]: <PenTool className="w-5 h-5" />,
  [DocumentType.CUSTOMER_VEHICLE_PHOTO]: <Image className="w-5 h-5" />,
  [DocumentType.LICENSE_PLATE_PHOTO]: <CreditCard className="w-5 h-5" />,
  [DocumentType.RECEIPT]: <FileText className="w-5 h-5" />,
  [DocumentType.OTHER]: <Paperclip className="w-5 h-5" />,
}

export function DocumentTypeSelector({
  isOpen,
  onSelect,
  onDismiss,
}: DocumentTypeSelectorProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on escape key or click outside
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onDismiss()
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onDismiss()
      }
    }

    window.addEventListener("keydown", handleEscape)
    window.addEventListener("mousedown", handleClickOutside)
    return () => {
      window.removeEventListener("keydown", handleEscape)
      window.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen, onDismiss])

  if (!isOpen) return null

  const documentTypes = Object.values(DocumentType)

  return (
    <div
      ref={menuRef}
      className="fixed bg-background rounded-lg shadow-lg border border-gray-200 py-2 z-50 animate-in fade-in zoom-in-95 duration-100"
      style={{
        minWidth: "240px",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      }}
    >
      {documentTypes.map((type, index) => (
        <button
          key={type}
          onClick={() => {
            onSelect(type)
            onDismiss()
          }}
          className={cn(
            "w-full px-4 py-2.5 text-left hover:bg-blue-50 transition-colors flex items-center gap-3 active:bg-blue-100",
            index !== documentTypes.length - 1 && "border-b border-gray-100"
          )}
        >
          <div className="text-gray-600">{DOCUMENT_TYPE_ICONS[type]}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-black truncate">{DOCUMENT_TYPE_LABELS[type]}</p>
          </div>
        </button>
      ))}
    </div>
  )
}
